/* ==========================================================================
   quickview.js — Shop All "Quick Add" hover button → QuickView modal.

   Flow:
     1) Delegated click on [data-quick-add] (a real <button> on each card)
        opens the modal and fetches the QuickView buy box for that product via
        the Section Rendering API:
            /products/{handle}?section_id=quickview-product
     2) The returned markup (sections/quickview-product.liquid) is injected into
        the modal. We then wire its buy box.
     3) Subscribe / one-time radios drive a hidden `selling_plan` input and
        repaint the modal price (scoped to the modal — never touches the page).
     4) Variant (size) tiles re-fetch the same section for the chosen variant
        (?variant={id}) and re-inject, mirroring the PDP variant-tile pattern.
     5) ATC submits to /cart/add.js (NOT a full-page form post) then opens the
        UpCart slide drawer — the Teeccino-wide cart pattern.

   Self-contained on purpose: pdp-hero.js is NOT loaded on the Shop All page,
   so this module owns the buy-box behavior here and shares no globals with the
   PDP. The ATC + UpCart logic intentionally mirrors pdp-hero.js so cart
   behavior is identical across surfaces.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'quickview-product';
  var modal, dialog, contentEl, closeBtn, backdrop;
  var lastTrigger = null;
  var keydownHandler = null;

  /* ---- UpCart open (mirrors pdp-hero.js openUpCart) --------------------- */
  function openUpCart() {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('upcart:open', { bubbles: true }));
    console.warn('[quickview] UpCart drawer not available — dispatched cart:refresh fallback.');
  }

  function cartAddUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';
  }

  /* ---- Analytics ------------------------------------------------------- *
     The Meta Pixel + GA4 run inside Shopify's Web Pixel sandbox, which only
     receives Shopify STANDARD events. A modal Quick View fires no standard
     `product_viewed`, and a modal ATC's `product_added_to_cart` may not reach
     every pixel. So we ALSO emit page-level analytics the theme can control:
       • GA4 via gtag (view_item / add_to_cart) — gtag is page-level here.
       • dataLayer.push (for GTM routing).
       • window.fbq guard — fires ViewContent/AddToCart IF a page-level Meta
         Pixel is ever present (no-op today; future-proof, never double-fires
         because fbq is currently sandbox-only / undefined on the page).
     The /cart/add.js path is preserved so Shopify's own AddToCart
     instrumentation continues to feed the sandboxed pixels too. */
  function qvNum(v) { var n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, '')); return isNaN(n) ? undefined : n; }

  function qvData() {
    var el = contentEl && contentEl.querySelector('[data-quickview]');
    if (!el) return null;
    return {
      id: el.getAttribute('data-qv-id') || '',
      variantId: el.getAttribute('data-qv-variant') || '',
      name: el.getAttribute('data-qv-name') || '',
      price: qvNum(el.getAttribute('data-qv-price')),
      currency: el.getAttribute('data-qv-currency') || 'USD',
      category: el.getAttribute('data-qv-category') || ''
    };
  }

  function trackQuickview(kind, overridePrice, quantity) {
    try {
      var d = qvData();
      if (!d) return;
      var value = (overridePrice != null ? overridePrice : d.price);
      var qty = quantity || 1;
      var ga4Event = kind === 'add_to_cart' ? 'add_to_cart' : 'view_item';
      var fbEvent = kind === 'add_to_cart' ? 'AddToCart' : 'ViewContent';
      var item = { item_id: d.id, item_name: d.name, item_variant: d.variantId, item_category: d.category, price: value, quantity: qty };
      var ga4Payload = { currency: d.currency, value: (value || 0) * qty, items: [item] };

      if (typeof window.gtag === 'function') window.gtag('event', ga4Event, ga4Payload);
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'quickview_' + ga4Event, ecommerce: ga4Payload, source: 'quickview' });
      }
      /* Meta event intentionally NOT fired here — see atc-tracking.js note.
         Shopify FB&IG channel owns the deduped browser+CAPI AddToCart. */
      // Forward-compatible DOM event a Shopify Custom Pixel can subscribe to.
      document.dispatchEvent(new CustomEvent('quickview:' + kind, { bubbles: true, detail: { item: item, currency: d.currency } }));
    } catch (e) { /* analytics must never break the modal */ }
  }

  /* ---- Modal scaffold (built once, reused) ----------------------------- */
  function buildModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'qv-modal';
    modal.setAttribute('hidden', '');
    modal.innerHTML =
      '<div class="qv-modal__backdrop" data-qv-backdrop></div>' +
      '<div class="qv-modal__dialog" role="dialog" aria-modal="true" aria-label="Quick add product" tabindex="-1" data-qv-dialog>' +
        '<button type="button" class="qv-modal__close" data-qv-close aria-label="Close quick add">' +
          '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<line x1="5" y1="5" x2="19" y2="19"></line><line x1="19" y1="5" x2="5" y2="19"></line>' +
          '</svg>' +
        '</button>' +
        '<div class="qv-modal__content" data-qv-content aria-live="polite"></div>' +
      '</div>';
    document.body.appendChild(modal);

    dialog = modal.querySelector('[data-qv-dialog]');
    contentEl = modal.querySelector('[data-qv-content]');
    closeBtn = modal.querySelector('[data-qv-close]');
    backdrop = modal.querySelector('[data-qv-backdrop]');

    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
  }

  function setLoading() {
    contentEl.innerHTML = '<div class="qv-modal__loading" role="status">' +
      '<span class="qv-modal__spinner" aria-hidden="true"></span>' +
      '<span class="visually-hidden">Loading…</span></div>';
  }

  /* ---- Focus trap ------------------------------------------------------ */
  function focusableEls() {
    return Array.prototype.slice.call(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeModal(); return; }
    if (e.key !== 'Tab') return;
    var els = focusableEls();
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openModal(trigger) {
    buildModal();
    lastTrigger = trigger || document.activeElement;
    modal.removeAttribute('hidden');
    // force reflow then add open class for the CSS transition
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.documentElement.classList.add('qv-modal-open');
    keydownHandler = onKeydown;
    document.addEventListener('keydown', keydownHandler);
    if (dialog) dialog.focus();
  }

  function closeModal() {
    if (!modal || modal.hasAttribute('hidden')) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('qv-modal-open');
    if (keydownHandler) { document.removeEventListener('keydown', keydownHandler); keydownHandler = null; }
    var done = function () {
      modal.setAttribute('hidden', '');
      contentEl.innerHTML = '';
      modal.removeEventListener('transitionend', done);
    };
    modal.addEventListener('transitionend', done);
    // Safety net if transitionend doesn't fire.
    setTimeout(function () { if (modal && modal.classList.contains('is-open') === false) done(); }, 350);
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  }

  /* ---- Fetch + inject -------------------------------------------------- */
  function buildSectionUrl(productUrlOrHandle, variantId) {
    var base;
    if (productUrlOrHandle.charAt(0) === '/') {
      base = new URL(productUrlOrHandle, window.location.origin);
    } else {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      base = new URL(root.replace(/\/$/, '') + '/products/' + productUrlOrHandle, window.location.origin);
    }
    base.searchParams.set('section_id', SECTION_ID);
    if (variantId) base.searchParams.set('variant', variantId);
    return base.toString();
  }

  function loadInto(url, isInitial) {
    setLoading();
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('section fetch ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // Section Rendering API returns the rendered section markup directly.
        contentEl.innerHTML = html;
        wireBuyBox();
        wireVariantTiles();
        wireGallery();
        // Product-view tracking — only on the initial open (not variant re-fetch).
        if (isInitial) trackQuickview('view_item');
        // move focus to the dialog so the trap has somewhere to land
        if (dialog) dialog.focus();
      })
      .catch(function (err) {
        console.error('[quickview] load failed:', err);
        contentEl.innerHTML = '<div class="qv-modal__error" role="alert">Sorry — could not load this product. ' +
          '<button type="button" class="qv-modal__error-close" data-qv-close>Close</button></div>';
        var c = contentEl.querySelector('[data-qv-close]');
        if (c) c.addEventListener('click', closeModal);
      });
  }

  /* ---- Buy box wiring (scoped to the modal) ---------------------------- */
  function wireBuyBox() {
    var form = contentEl.querySelector('[data-qv-buybox]');
    if (!form) return;

    var sellingPlanInput = form.querySelector('[data-qv-buybox-selling-plan]');
    var radios = form.querySelectorAll('[data-qv-buybox-radio]');
    var submitBtn = form.querySelector('[data-qv-atc-button]');
    var submitText = form.querySelector('.qv-buybox__submit-text');
    var errorSlot = form.querySelector('[data-qv-buybox-error]');

    var priceEl = contentEl.querySelector('[data-qv-price]');
    var compareEl = contentEl.querySelector('[data-qv-compare-at]');
    var saveEl = contentEl.querySelector('[data-qv-save-pill]');

    // IMPORTANT (2026-06-06): do NOT mutate the price/compare/save elements.
    // The Intelligems price-testing app (live only — it doesn't run on preview
    // themes) has an observer on price elements; when we rewrote the modal's
    // price text it synchronously DESTROYED the modal content (collapsed to just
    // the price), so the buy box vanished on every open. The big price stays at
    // its server-rendered value (correct for the default one-time option) and
    // each option card already shows its own price (subscribe vs one-time), so
    // no information is lost. The selling_plan sync below is what actually drives
    // the correct add-to-cart. Re-enabling a live price repaint requires
    // excluding the QuickView modal from Intelligems (app config), not theme JS.
    function applyPriceFromRadio(radio) { /* intentionally no-op — see note above */ }

    Array.prototype.forEach.call(radios, function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        if (sellingPlanInput) sellingPlanInput.value = radio.getAttribute('data-selling-plan') || '';
        applyPriceFromRadio(radio);
      });
    });
    // Sync hidden selling_plan AND the displayed price to whichever radio is
    // checked at injection (subscribe is pre-selected) — otherwise the big
    // price would show the one-time figure while Subscribe is the active row.
    var checked = form.querySelector('[data-qv-buybox-radio]:checked');
    if (checked) {
      if (sellingPlanInput) sellingPlanInput.value = checked.getAttribute('data-selling-plan') || '';
      applyPriceFromRadio(checked);
    }

    var originalText = submitText ? submitText.textContent : 'ADD TO CART';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!submitBtn || submitBtn.disabled) return;
      if (errorSlot) { errorSlot.hidden = true; errorSlot.textContent = ''; }
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      if (submitText) submitText.textContent = 'ADDING…';

      var formData = new FormData(form);
      // Attribute this add to the collection quick-add surface; the site-wide
      // atc-tracking.js interceptor reads this and fires the add_to_cart event.
      if (window.TeeccinoATC) window.TeeccinoATC.setSource('collection-quickadd');
      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin'
      })
        .then(function (res) { return res.json().then(function (body) { return { ok: res.ok, body: body }; }); })
        .then(function (result) {
          if (!result.ok) {
            var message = (result.body && (result.body.description || result.body.message)) ||
              'Could not add to cart — please try again.';
            if (errorSlot) { errorSlot.textContent = message; errorSlot.hidden = false; }
            return;
          }
          // add_to_cart tracking is now handled centrally by atc-tracking.js
          // (it intercepts this /cart/add.js call and attributes it to the
          // 'collection-quickadd' source set above) — no per-modal fire here,
          // which would double-count.
          closeModal();
          openUpCart();
          if (window.PUB_SUB_EVENTS && window.publish) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'quickview', productVariantId: formData.get('id') });
          }
        })
        .catch(function (err) {
          console.error('[quickview] ATC failed:', err);
          if (errorSlot) { errorSlot.textContent = 'Network error — please try again.'; errorSlot.hidden = false; }
        })
        .finally(function () {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          if (submitText) submitText.textContent = originalText;
        });
    });
  }

  /* ---- Image gallery → thumbnail click swaps the main image ------------ */
  function wireGallery() {
    var gallery = contentEl.querySelector('[data-qv-gallery]');
    if (!gallery) return;
    var mainImg = gallery.querySelector('.qv__media-main img');
    var thumbs = gallery.querySelectorAll('[data-qv-thumb]');
    if (!mainImg || thumbs.length < 2) return;
    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full');
        if (full) {
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = full;
        }
        Array.prototype.forEach.call(thumbs, function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ---- Variant tiles → re-fetch the section for the chosen variant ----- */
  function wireVariantTiles() {
    var qv = contentEl.querySelector('[data-quickview]');
    var tiles = contentEl.querySelectorAll('[data-qv-variant-tile]');
    Array.prototype.forEach.call(tiles, function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        if (tile.classList.contains('is-selected')) return;
        var variantId = tile.getAttribute('data-variant-id');
        var handle = (qv && qv.getAttribute('data-product-handle')) || '';
        var url = (qv && qv.getAttribute('data-product-url')) || handle;
        loadInto(buildSectionUrl(url || handle, variantId));
      });
    });
  }

  /* ---- Delegated trigger ----------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    e.preventDefault();
    var handle = trigger.getAttribute('data-quick-add-handle');
    if (!handle) return;
    openModal(trigger);
    loadInto(buildSectionUrl(handle, null), true);
  });
})();
